import { Me, User } from '../../models/User'
import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { selectEmailThreads } from './emailThreads'

interface UserState {
  isCurrentUserLoading: boolean
  currentUser: Me | null
  contacts: EntityState<User, string>
  contactsLoading: boolean
}

const emailContactsAdapter = createEntityAdapter<User>()

const initialState: UserState = {
  isCurrentUserLoading: true,
  currentUser: null,
  contacts: emailContactsAdapter.getInitialState(),
  contactsLoading: true,
}

export const emailUserSlice = createSlice({
  name: 'emailUsers',
  initialState,
  reducers: {
    setEmailCurrentUserLoading: (state, action: PayloadAction<boolean>) => {
      state.isCurrentUserLoading = action.payload
    },
    setCurrentUser: (state, action: PayloadAction<Me>) => {
      state.currentUser = action.payload
    },
    setEmailContactsLoading: (state, action: PayloadAction<boolean>) => {
      state.contactsLoading = action.payload
    },
    setEmailContacts: (state, action: PayloadAction<User[]>) => {
      emailContactsAdapter.setAll(state.contacts, action.payload)
    },
    upsertEmailContacts: (state, action: PayloadAction<User[]>) => {
      emailContactsAdapter.upsertMany(state.contacts, action.payload)
    },
    resetEmailUsers: () => initialState,
  },
})

export const { setEmailCurrentUserLoading, setCurrentUser, setEmailContactsLoading, setEmailContacts, upsertEmailContacts, resetEmailUsers } =
  emailUserSlice.actions

const emailContactSelectors = emailContactsAdapter.getSelectors((state: RootState) => state.emailHub.emailUsers.contacts)

export const selectCurrentUserEmailIntegration = (state: RootState) => {
  const currentUser = state.emailHub.emailUsers.currentUser
  if (!currentUser) {
    return null
  }
  return currentUser.integrations?.find((integration) => integration.type === 'mail') ?? null
}

export const selectEmailContacts = (state: RootState) => emailContactSelectors.selectAll(state)

export const selectEmailCurrentUserLoading = (state: RootState) => state.emailHub.emailUsers.isCurrentUserLoading

export const selectEmailCurrentUser = (state: RootState) => state.emailHub.emailUsers.currentUser

export const selectEmailContactsLoading = (state: RootState) => state.emailHub.emailUsers.contactsLoading

export const selectContactsForThread = (threadId: string) =>
  createSelector([selectEmailThreads, selectEmailContacts], (threads, contacts): User[] => {
    const thread = threads.find((t) => t.externalId === threadId)
    if (!thread) {
      return []
    }

    const uniqueEmails = thread.emails.reduce<string[]>((acc, email) => {
      const { sender, recipients, ccRecipients } = email
      const allEmails = [sender, ...recipients, ...ccRecipients].map((e) => e.email.toLowerCase())
      const uniqueEmails = allEmails.filter((e) => !acc.includes(e))
      return [...acc, ...uniqueEmails]
    }, [])

    return uniqueEmails.reduce<User[]>((acc, email) => {
      const contact = contacts.find((c) => c.email.toLowerCase() === email)
      if (!contact) {
        return acc
      }
      return [...acc, contact]
    }, [])
  })
